import {AnyAction, combineReducers, configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {PersistConfig, PersistedState, persistReducer} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import localStorage from 'redux-persist/lib/storage';
import commonReducer, {clearAuthInfo} from '../stores/common/commonSlice';
import {
    authApi,
    bexioAccountsApi,
    businessesApi,
    chargersApi,
    companyApi,
    contractsApi,
    customInvoiceItemsApi,
    employeeApi,
    guestApi,
    invoicesApi,
    locationsApi,
    notificationApi,
    ocppApi,
    pricingApi,
    registerApi,
    reOwnerApi,
    reportsApi,
    systemApi,
    tagsApi,
    tariffsApi,
    transactionsApi,
    usersApi,
    reportServiceApi,
    aiAgentApi,
    iamApi,
} from 'src/api';

const rootReducer = combineReducers({
    common: commonReducer,
    [authApi.reducerPath]: authApi.reducer,
    [bexioAccountsApi.reducerPath]: bexioAccountsApi.reducer,
    [customInvoiceItemsApi.reducerPath]: customInvoiceItemsApi.reducer,
    [registerApi.reducerPath]: registerApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [locationsApi.reducerPath]: locationsApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [chargersApi.reducerPath]: chargersApi.reducer,
    [tariffsApi.reducerPath]: tariffsApi.reducer,
    [contractsApi.reducerPath]: contractsApi.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [businessesApi.reducerPath]: businessesApi.reducer,
    [invoicesApi.reducerPath]: invoicesApi.reducer,
    [ocppApi.reducerPath]: ocppApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [systemApi.reducerPath]: systemApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [reOwnerApi.reducerPath]: reOwnerApi.reducer,
    [tagsApi.reducerPath]: tagsApi.reducer,
    [employeeApi.reducerPath]: employeeApi.reducer,
    [pricingApi.reducerPath]: pricingApi.reducer,
    [guestApi.reducerPath]: guestApi.reducer,
    [reportServiceApi.reducerPath]: reportServiceApi.reducer,
    [aiAgentApi.reducerPath]: aiAgentApi.reducer,
    [iamApi.reducerPath]: iamApi.reducer,
});

/**
 * Everything cached is cached for one person.
 *
 * RTK Query keys its cache by endpoint and argument, not by who asked, so signing out and back in as
 * somebody else served the previous person's data from cache — including `/me/permissions`, which is
 * what the whole interface is drawn from. The result was a sidebar built from the last session's
 * rights over a server that correctly refused every item on it.
 *
 * Dropping every cache slice at once is the only version of this that cannot be got wrong one slice
 * at a time; `combineReducers` re-initialises whatever is missing. `common` is kept and left to its
 * own `clearAuthInfo` reducer, because the chosen language lives there and has nothing to do with
 * who is signed in.
 */
const appReducer = (state: ReturnType<typeof rootReducer> | undefined, action: AnyAction) =>
    rootReducer(
        state && action.type === clearAuthInfo.type
            ? ({common: state.common} as ReturnType<typeof rootReducer>)
            : state,
        action
    );

const PERSISTED_STATE_VERSION = 2;

/**
 * How a saved state from an older build is allowed to come back.
 *
 * Two separate things go wrong here, and both bit us:
 *
 * The default reconciler is `autoMergeLevel1`, which *replaces* `common` wholesale with whatever was
 * stored - `newState[key] = inboundState[key]`. A state written before `authInfo` existed therefore
 * came back with no `authInfo` at all, taking the slice's own `{}` with it, and the forty-odd
 * `Object.keys(authInfo)` calls across the application threw on the first render. `autoMergeLevel2`
 * merges one level deeper, so a key the stored slice never had keeps its initial value. That is the
 * fix that also covers the next key somebody adds to `common`.
 *
 * And `version` on its own does nothing: without a `migrate`, redux-persist defaults to
 * `(state) => Promise.resolve(state)` and rehydrates the old state unchanged whatever the number says.
 * The migration below is what actually discards it. Returning `undefined` means "no persisted state",
 * and the reducer starts from its own initial values.
 *
 * Raise the version whenever the shape of anything in `common` changes. It costs one sign-in, and it
 * is the only way to reach somebody whose browser is already holding a state this build cannot read -
 * they see a blank page and have no reason to suspect their own storage.
 */
const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
    key: 'root',
    version: PERSISTED_STATE_VERSION,
    storage: localStorage,
    whitelist: ['common'],
    stateReconciler: autoMergeLevel2,
    migrate: (state: PersistedState) =>
        Promise.resolve(state?._persist?.version === PERSISTED_STATE_VERSION ? state : undefined),
};

export const store = configureStore({
    reducer: persistReducer(persistConfig, appReducer),
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat([
            authApi.middleware,
            bexioAccountsApi.middleware,
            customInvoiceItemsApi.middleware,
            registerApi.middleware,
            companyApi.middleware,
            locationsApi.middleware,
            usersApi.middleware,
            chargersApi.middleware,
            tariffsApi.middleware,
            contractsApi.middleware,
            transactionsApi.middleware,
            businessesApi.middleware,
            invoicesApi.middleware,
            ocppApi.middleware,
            reportsApi.middleware,
            systemApi.middleware,
            notificationApi.middleware,
            reOwnerApi.middleware,
            tagsApi.middleware,
            employeeApi.middleware,
            pricingApi.middleware,
            guestApi.middleware,
            reportServiceApi.middleware,
            aiAgentApi.middleware,
            iamApi.middleware,
        ]),
});

/**
 * What makes `refetchOnFocus` and `refetchOnReconnect` mean anything.
 *
 * Nothing dispatches the events those two react to until this call puts the listeners on the window -
 * `focus` and `visibilitychange` for the first, `online` and `offline` for the second. Set a flag
 * without it and it is simply never consulted, so the setting would look enabled and do nothing.
 *
 * Both are off in `REFETCH_POLICY` now (`src/axiosBaseQuery/index.ts` says why), so this call enables
 * nothing by itself today. It stays because those flags are per-query as much as per-slice: a screen
 * that passes `{refetchOnFocus: true}` needs these listeners, and finding out that a setting was inert
 * all along is exactly the afternoon this comment exists to save.
 *
 * The unsubscribe it returns is dropped on purpose: the listeners are meant to live as long as the
 * page does.
 */
setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
